import { cn } from "@babackapp/ui/lib/utils";
import { XIcon } from "lucide-react";
import React from "react";
import { useIsNodeSelected } from "../context/node-selection-context";
import { useNodeById } from "../hooks/use-node-by-id";
import useNodePalette from "../hooks/use-node-palette";
import { useIsWorkflowsListingMode } from "../context/workflows-editor-workflows-context";
import { useTranslation } from "react-i18next";

interface BaseNodeProps extends React.ComponentProps<"div"> {
  nodeId?: string;
  hideHeader?: boolean;
  title?: string;
}

export function BaseNode({
  nodeId,
  hideHeader = false,
  children,
  title,
  ...restProps
}: BaseNodeProps) {
  const isSelected = useIsNodeSelected(nodeId) || false;
  const { nodeConfig, editorNodeType } = useNodeById(nodeId || "");
  const isWorkflowsListingMode = useIsWorkflowsListingMode();
  const { classes } = useNodePalette(editorNodeType!);
  const { t } = useTranslation("trackoffice");

  const Icon = nodeConfig?.config.Icon;
  const nodeName = nodeConfig?.config.name;

  return (
    <div
      {...restProps}
      className={cn(
        "flex flex-col shadow-md rounded-md bg-background border min-w-[200px] max-w-[250px]",
        isSelected && "ring-2 ring-primary",
        isWorkflowsListingMode && "pointer-events-none cursor-default",
        restProps.className,
      )}
    >
      {!hideHeader && (
        <div
          className={cn(
            "flex border-b bg-default/5 text-default p-2 cursor-pointer",
            classes,
          )}
        >
          {Icon && <Icon className="size-4 mr-2" />}
          <span className="text-xs font-semibold flex-1">
            {t(title || nodeName || "", { defaultValue: title || nodeName })}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

export function BaseNodeHeader(props: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "flex border-b bg-default/5 text-default p-2 cursor-pointer",
        props.className,
      )}
    />
  );
}

export function BaseNodeFooter(props: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "flex border-t bg-muted/50 text-foreground px-2 py-1 cursor-default",
        props.className,
      )}
    />
  );
}

export function BaseNodeTitle(props: React.ComponentProps<"span">) {
  return (
    <span
      {...props}
      className={cn("text-xs font-semibold flex-1", props.className)}
    />
  );
}

export function BaseNodeCloseButton(props: React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      {...props}
      title="Remove email action"
      className={cn(
        "nodrag flex h-4 w-4 items-center justify-center rounded hover:bg-primary/80 transition-colors cursor-pointer hover:text-primary-foreground",
        props.className,
      )}
    >
      <XIcon className="size-4" />
    </button>
  );
}

export function BaseNodeContent(props: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "relative text-sm p-2 py-3 relative w-full cursor-default",
        props.className,
      )}
    />
  );
}
